/**
 * GST UQC (Unit Quantity Code) master list — mirrors api/internal/billing/unit_types.go
 * These are the official CBIC-notified codes required on GST invoices.
 */
export const MASTER_UNIT_CODES = [
  { code: 'BAG', name: 'Bags' },
  { code: 'BAL', name: 'Bale' },
  { code: 'BDL', name: 'Bundles' },
  { code: 'BKL', name: 'Buckles' },
  { code: 'BOU', name: 'Billions of Units' },
  { code: 'BOX', name: 'Box' },
  { code: 'BTL', name: 'Bottles' },
  { code: 'BUN', name: 'Bunches' },
  { code: 'CAN', name: 'Cans' },
  { code: 'CBM', name: 'Cubic Meters' },
  { code: 'CCM', name: 'Cubic Centimeters' },
  { code: 'CMS', name: 'Centimeters' },
  { code: 'CTN', name: 'Cartons' },
  { code: 'DOZ', name: 'Dozens' },
  { code: 'DRM', name: 'Drums' },
  { code: 'GGR', name: 'Great Gross' },
  { code: 'GMS', name: 'Grams' },
  { code: 'GRS', name: 'Gross' },
  { code: 'GYD', name: 'Gross Yards' },
  { code: 'KGS', name: 'Kilograms' },
  { code: 'KLR', name: 'Kiloliters' },
  { code: 'KME', name: 'Kilometers' },
  { code: 'LTR', name: 'Liters' },
  { code: 'MLT', name: 'Milliliters' },
  { code: 'MTR', name: 'Meters' },
  { code: 'MTS', name: 'Metric Tons' },
  { code: 'NOS', name: 'Numbers' },
  { code: 'OTH', name: 'Others' },
  { code: 'PAC', name: 'Packs' },
  { code: 'PCS', name: 'Pieces' },
  { code: 'PRS', name: 'Pairs' },
  { code: 'QTL', name: 'Quintals' },
  { code: 'ROL', name: 'Rolls' },
  { code: 'SET', name: 'Sets' },
  { code: 'SQF', name: 'Square Feet' },
  { code: 'SQM', name: 'Square Meters' },
  { code: 'SQY', name: 'Square Yards' },
  { code: 'TBS', name: 'Tablets' },
  { code: 'TGM', name: 'Ten Gross' },
  { code: 'THD', name: 'Thousands' },
  { code: 'TON', name: 'Tonnes' },
  { code: 'TUB', name: 'Tubes' },
  { code: 'UGS', name: 'US Gallons' },
  { code: 'UNT', name: 'Units' },
  { code: 'YDS', name: 'Yards' },
]

// Build a lookup map for O(1) name resolution
const _unitNameMap = Object.fromEntries(MASTER_UNIT_CODES.map((u) => [u.code, u.name]))

/**
 * Look up the display name for a UQC code.
 * Falls back to the raw code if not found.
 *
 * @param {string} code - UQC code e.g. "PCS"
 * @returns {string} display name e.g. "Pieces"
 */
export const getUnitName = (code) => {
  if (!code) return ''
  return _unitNameMap[code] || code
}

/**
 * Build select-dropdown options from an array of enabled code strings.
 * Label format: "Pieces (PCS)"
 *
 * @param {string[]} enabledCodes - e.g. ["PCS", "KGS", "LTR"]
 * @returns {{ value: string, label: string }[]}
 */
export const buildUnitOptions = (enabledCodes) => {
  if (!enabledCodes || enabledCodes.length === 0) return []
  const codeSet = new Set(enabledCodes)
  return MASTER_UNIT_CODES.filter((u) => codeSet.has(u.code)).map((u) => ({
    value: u.code,
    label: `${u.name} (${u.code})`,
  }))
}

/**
 * Groupings for the settings UI — determines how checkboxes are visually clustered.
 */
export const UNIT_CODE_GROUPS = [
  {
    label: 'Weight',
    codes: ['GMS', 'KGS', 'MTS', 'QTL', 'TON'],
  },
  {
    label: 'Length',
    codes: ['CMS', 'MTR', 'KME', 'YDS', 'GYD'],
  },
  {
    label: 'Volume',
    codes: ['MLT', 'LTR', 'KLR', 'UGS', 'CBM', 'CCM'],
  },
  {
    label: 'Area',
    codes: ['SQF', 'SQM', 'SQY'],
  },
  {
    label: 'Count / Measure',
    codes: ['NOS', 'PCS', 'UNT', 'DOZ', 'GRS', 'GGR', 'TGM', 'THD', 'BOU', 'PRS', 'SET', 'TBS'],
  },
  {
    label: 'Container / Pack',
    codes: ['BAG', 'BAL', 'BDL', 'BKL', 'BOX', 'BTL', 'BUN', 'CAN', 'CTN', 'DRM', 'PAC', 'ROL', 'TUB', 'OTH'],
  },
]
